<template>
  <div class="ui stackable grid">
    <div class="ten wide column">
      <div v-if="showImages">
        <div
          class="ui keyword segment"
          :class="{ attached: isHost }"
          v-if="isHost || reviewModeEnabled"
        >
          <h1 class="ui header">
            Foto {{ activePhoto.id + 1 }}: {{ activePhoto.answer }}
            <div
              class="ui green horizontal label"
              v-if="keywords[activePhoto.id].found"
            >
              {{ keywords[activePhoto.id].foundBy | middleTruncate }}
            </div>
          </h1>
        </div>
        <div
          class="ui two bottom attached huge buttons"
          v-if="isHost && reviewModeEnabled"
        >
          <div
            class="ui bottom big orange button"
            :class="{ disabled: activePhoto.id < 1 }"
            id="prev-image"
            @click.stop="activePhoto.id > 0 && prevImage()"
          >
            <i class="ui left arrow icon"></i> Vorige Afbeelding
          </div>
          <div
            class="ui bottom big orange button"
            id="next-image"
            @click.stop="nextImage"
          >
            Volgende Afbeelding <i class="ui right arrow icon"></i>
          </div>
        </div>
        <div class="ui two bottom attached huge buttons" v-else-if="isHost">
          <div
            class="ui negative big button"
            id="wrong-answer"
            @click.stop="
              answerQuestion({ keywordId: activePhoto.id, answer: false })
            "
          >
            <i class="remove icon"></i> [PIJLTJE LINKS]
          </div>
          <div
            class="ui positive big button"
            id="correct-answer"
            @click.stop="
              answerQuestion({ keywordId: activePhoto.id, answer: true })
            "
          >
            <i class="checkmark icon"></i> [PIJLTJE RECHTS]
          </div>
        </div>
      </div>
      <div
        class="ui basic segment image-container"
        :class="{ 'no-spacing': !isHost && !reviewModeEnabled }"
        v-if="showImages"
      >
        <transition name="fade" mode="out-in" :appear="true">
          <img
            class="ui rounded centered image"
            :key="activePhoto.id"
            :src="activePhoto.url"
          />
        </transition>
      </div>
      <keyword-panel
        :keywords="keywords"
        @clickedKeyword="clickedKeyword"
        v-else-if="isHost"
      ></keyword-panel>
      <div class="ui center aligned fluid container" v-else>
        <transition name="fade" mode="out-in" :appear="true">
          <div class="ui very padded segment">
            <div
              class="ui top active attached indicating progress"
              :data-percent="foundKeywordsPercentage"
            >
              <div
                class="bar"
                :style="'width: ' + foundKeywordsPercentage + '%;'"
              ></div>
            </div>
            <div class="ui huge statistic">
              <div class="value">
                {{ nrOfFoundKeywords }}/{{ nrOfKeywords }}
              </div>
              <div class="label">foto's gevonden</div>
            </div>
          </div>
        </transition>
      </div>
    </div>
    <div class="six wide column">
      <scoreboard-vertical
        :show-controls="isHost && !showImages"
      ></scoreboard-vertical>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import ScoreboardVertical from '@/components/elements/scoreboardVertical';
import KeywordPanel from '@/components/elements/keywordPanel';
import { filter as _filter } from 'lodash';
import sounds from '@/sounds';
import Mousetrap from 'mousetrap';
import alertify from 'alertify.js';

export default {
  name: 'galerij',
  components: { ScoreboardVertical, KeywordPanel },
  computed: {
    ...mapGetters(['reviewModeEnabled', 'isHost', 'isPremium']),
    ...mapGetters('galerij', ['activeQuestion', 'activePhoto', 'showImages']),
    keywords() {
      return this.showImages
        ? this.$store.getters['galerij/activeKeywords']
        : this.$store.getters['galerij/activeKeywordsSortedByFound'];
    },
    nrOfKeywords() {
      return this.keywords.length;
    },
    nrOfFoundKeywords() {
      return _filter(this.keywords, keyword => keyword.found).length;
    },
    foundKeywordsPercentage() {
      return this.nrOfFoundKeywords * 10;
    }
  },
  created() {
    this.addKeyboardShortcuts();
  },
  methods: {
    nextImage() {
      this.$store.dispatch('galerij/nextImage');
      this.$store.dispatch('syncGame');
    },
    prevImage() {
      this.$store.dispatch('galerij/prevImage');
      this.$store.dispatch('syncGame');
    },
    clickedKeyword(keywordId) {
      this.answerQuestion({ keywordId, answer: true });
    },
    answerQuestion({ keywordId, answer }) {
      this.$store.dispatch('galerij/answerQuestion', { keywordId, answer });
      this.$store.dispatch('syncGame');
      if (this.isHost)
        sounds.emitSound(answer ? sounds.AUDIO.CORRECT : sounds.AUDIO.WRONG);
    },
    addKeyboardShortcuts() {
      // Blindly click the buttons...
      if (this.isPremium) {
        Mousetrap.bind('left', () => {
          const leftButton =
            document.querySelector('#prev-image') ||
            document.querySelector('#wrong-answer');
          if (leftButton) leftButton.click();
          return false;
        });
        Mousetrap.bind('right', () => {
          const rightButton =
            document.querySelector('#next-image') ||
            document.querySelector('#correct-answer');
          if (rightButton) rightButton.click();
          return false;
        });
      } else {
        Mousetrap.bind(['left', 'right'], () => {
          alertify.log(
            'Deze sneltoets is niet mogelijk in de gratis basisversie.'
          );
        });
      }
    }
  }
};
</script>

<style scoped>
.ui.statistic {
  margin: 0;
}
.ui.statistic > .value {
  font-size: 18rem !important;
  margin-bottom: 3rem;
}
.ui.statistic > .label {
  font-size: 3rem !important;
}
@media only screen and (max-width: 991px) {
  .ui.statistic > .value {
    font-size: 8rem !important;
  }
  .ui.statistic > .label {
    font-size: 2rem !important;
  }
}
.bar {
  transition-duration: 300ms;
  -webkit-transition-duration: 300ms;
}
.image-container {
  height: 600px;
  margin-top: 2rem;
  padding: 0;
}
.image-container.no-spacing {
  margin-top: 0rem;
}
.image-container .ui.image {
  max-height: 100%;
  box-shadow: 0px 0px 15px rgba(0, 0, 0, 0.3);
}
.keyword .ui.header {
  margin-top: 0.23rem;
}
</style>
